/**
 * Merge prompt shown when browser-local saved itineraries are detected that do
 * not exist in the authenticated user's profile.
 *
 * The extras are already flagged `localOnly` and merged into the local slice
 * before this prompt opens — closing it (secondary action, backdrop, close
 * icon) only defers the decision and keeps the entries on the device; they are
 * offered again at the next login. The primary action pushes them into the
 * profile.
 */

import React, { useCallback, useMemo, useState } from "react";
import { getUIColorPalette } from "../theme/ColorPalette";
import Modal from "./Modal/Modal";

/** Popup title. */
const POPUP_TITLE = "Trajets favoris sur cet appareil";

/** Popup message — account level only, no itinerary list. */
const POPUP_MESSAGE = "Des trajets favoris sont enregistrés sur cet appareil mais n'apparaissent pas dans votre profil. Voulez-vous les ajouter à votre compte ? Sinon, ils resteront uniquement sur cet appareil.";

/** Props for {@link SavedItinerariesMergePrompt}. */
export interface SavedItinerariesMergePromptProps {

    /** Whether the prompt is currently visible. */
    "isOpen": boolean;

    /**
     * Pushes the detected local extras into the authenticated profile.
     * Resolves after the merge attempt completes (success or handled failure).
     */
    "onSyncToProfile": () => Promise<void>;

    /** Defers the decision — extras stay flagged `localOnly` on this device. */
    "onClose": () => void;

    /** Whether the dark UI palette is active. */
    "isDarkUi": boolean;
}

/**
 * Displays a modal proposing to merge device-local favorites into the profile.
 * Both dismissal paths keep the extras locally — only the primary action syncs.
 *
 * @param props - Component props.
 * @returns The merge prompt modal, or `null` when closed.
 */
export const SavedItinerariesMergePrompt: React.FC<SavedItinerariesMergePromptProps> = ( {
    isOpen,
    onSyncToProfile,
    onClose,
    isDarkUi,
} ) => {
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );
    const [ isSyncing, setIsSyncing ] = useState<boolean>( false );

    /**
     * Runs the profile merge, keeping the buttons disabled while in flight.
     */
    const handleSync = useCallback( () => {
        if ( isSyncing ) {
            return;
        }

        setIsSyncing( true );
        void onSyncToProfile().finally( () => {
            setIsSyncing( false );
        } );
    }, [ isSyncing, onSyncToProfile ] );

    return (
        <Modal
            isDarkUi={isDarkUi}
            isOpen={isOpen}
            onClose={() => {
                if ( !isSyncing ) {
                    onClose();
                }
            }}
        >
            <div className="flex flex-col items-center gap-4 text-center px-2 pt-2">
                <h2
                    className="font-bold text-lg"
                    style={{ "color": palette.brandPrimary }}
                >
                    {POPUP_TITLE}
                </h2>
                <p
                    className="text-sm leading-relaxed"
                    style={{ "color": palette.modalCardTextPrimary }}
                >
                    {POPUP_MESSAGE}
                </p>
                <div className="flex flex-col w-full gap-3 mt-2">
                    <button
                        className="w-full rounded-full px-4 py-2.5 text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-60"
                        disabled={isSyncing}
                        onClick={handleSync}
                        style={{
                            "backgroundColor": palette.brandPrimary,
                            "color":           palette.textOnBrandPrimary,
                        }}
                        type="button"
                    >
                        Ajouter à mon profil
                    </button>
                    <button
                        className="w-full rounded-full px-4 py-2.5 text-sm font-medium transition-colors disabled:opacity-60"
                        disabled={isSyncing}
                        onClick={onClose}
                        onMouseEnter={( event ) => {
                            event.currentTarget.style.backgroundColor = palette.overlayBorder;
                        }}
                        onMouseLeave={( event ) => {
                            event.currentTarget.style.backgroundColor = palette.interactiveHoverBackground;
                        }}
                        style={{
                            "backgroundColor": palette.interactiveHoverBackground,
                            "color":           palette.modalCardTextPrimary,
                        }}
                        type="button"
                    >
                        Garder seulement sur cet appareil
                    </button>
                </div>
            </div>
        </Modal>
    );
};
